import React, { useState, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, Upload, Sparkles, Download, Share2, RotateCcw, User, Shirt, X, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import auraLogo from '@/assets/aura-logo.png';
import { useStyleCredits, CREDIT_COSTS } from '@/hooks/useStyleCredits';
import { InsufficientCreditsModal } from '@/components/modals/InsufficientCreditsModal';

interface VirtualTryOnProps {
  isOpen: boolean;
  onClose: () => void;
  onBuyCredits?: () => void;
  outfit?: {
    top?: { name: string; image_url?: string; color?: string };
    bottom?: { name: string; image_url?: string; color?: string };
    shoes?: { name: string; image_url?: string; color?: string };
  };
}

export const VirtualTryOn: React.FC<VirtualTryOnProps> = ({ isOpen, onClose, onBuyCredits, outfit }) => {
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { balance, refreshBalance } = useStyleCredits();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUserPhoto(event.target?.result as string);
      setStep('preview');
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!userPhoto || !outfit) return;
    
    // Check credits first
    if (balance < CREDIT_COSTS.virtual_tryon) {
      setShowInsufficientCredits(true);
      return;
    }

    setIsGenerating(true);
    
    try {
      // Build outfit description
      const outfitDesc = [
        outfit.top && `${outfit.top.color || ''} ${outfit.top.name}`.trim(),
        outfit.bottom && `${outfit.bottom.color || ''} ${outfit.bottom.name}`.trim(),
        outfit.shoes && `${outfit.shoes.color || ''} ${outfit.shoes.name}`.trim(),
      ].filter(Boolean).join(', ');

      const { data, error } = await supabase.functions.invoke('virtual-tryon', {
        body: {
          userPhoto,
          outfitDescription: outfitDesc,
          outfitItems: outfit,
        },
      });

      if (error) throw error;

      // Handle insufficient credits from server
      if (data.error === 'insufficient_credits') {
        setShowInsufficientCredits(true);
        refreshBalance();
        setIsGenerating(false);
        return;
      }

      if (data.generatedImage) {
        setGeneratedImage(data.generatedImage);
        setStep('result');
        refreshBalance();
        toast.success('Virtual try-on complete!');
      } else {
        throw new Error('No image generated');
      }
    } catch (error) {
      console.error('Virtual try-on error:', error);
      toast.error('Failed to generate try-on. Please try again.');
      refreshBalance();
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = 'aura-virtual-tryon.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const handleShare = async () => {
    if (!generatedImage) return;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My AURA Look',
          text: 'Check out this outfit I created with AURA!',
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleReset = () => {
    setUserPhoto(null);
    setGeneratedImage(null);
    setStep('upload');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 bg-background border-border overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={auraLogo} alt="AURA" className="w-8 h-8 object-contain" />
            <div>
              <h2 className="text-lg font-display tracking-wider text-foreground">Virtual Try-On</h2>
              <p className="text-xs text-muted-foreground">AI-Powered Outfit Visualization</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-aura-gold/20 to-aura-rose/20 flex items-center justify-center">
                <User size={32} className="text-foreground" />
              </div>
              
              <div>
                <h3 className="text-xl font-serif text-foreground mb-2">Upload Your Photo</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Upload a full-body photo to see how this outfit would look on you.
                </p>
              </div>

              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-foreground text-background hover:bg-accent hover:text-accent-foreground"
                >
                  <Upload size={18} className="mr-2" />
                  Choose Photo
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-border"
                >
                  <Camera size={18} className="mr-2" />
                  Take a Photo
                </Button>
              </div>

              {/* Outfit Preview */}
              {outfit && (
                <div className="mt-6 p-4 bg-card rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground mb-3">Selected Outfit</p>
                  <div className="flex justify-center gap-4">
                    {outfit.top && (
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                          {outfit.top.image_url ? (
                            <img src={outfit.top.image_url} alt={outfit.top.name} className="w-full h-full object-cover" />
                          ) : (
                            <Shirt size={24} className="text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-xs text-foreground mt-1 truncate w-16">{outfit.top.name}</p>
                      </div>
                    )}
                    {outfit.bottom && (
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                          {outfit.bottom.image_url ? (
                            <img src={outfit.bottom.image_url} alt={outfit.bottom.name} className="w-full h-full object-cover" />
                          ) : (
                            <Shirt size={24} className="text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-xs text-foreground mt-1 truncate w-16">{outfit.bottom.name}</p>
                      </div>
                    )}
                    {outfit.shoes && (
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                          {outfit.shoes.image_url ? (
                            <img src={outfit.shoes.image_url} alt={outfit.shoes.name} className="w-full h-full object-cover" />
                          ) : (
                            <Shirt size={24} className="text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-xs text-foreground mt-1 truncate w-16">{outfit.shoes.name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                📸 For best results, use a well-lit photo with a neutral background
              </p>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="aspect-[3/4] max-h-[50vh] rounded-xl overflow-hidden bg-muted relative">
                {userPhoto && (
                  <img 
                    src={userPhoto} 
                    alt="Your photo" 
                    className="w-full h-full object-contain"
                  />
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 border-border"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Change Photo
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="flex-1 bg-gradient-to-r from-aura-gold to-aura-rose text-background hover:opacity-90"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} className="mr-2" />
                      Generate Try-On
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 'result' && (
            <div className="space-y-6">
              <div className="aspect-[3/4] max-h-[50vh] rounded-xl overflow-hidden bg-muted relative">
                {generatedImage && (
                  <img 
                    src={generatedImage} 
                    alt="Virtual try-on result" 
                    className="w-full h-full object-contain"
                  />
                )}
                <div className="absolute top-3 left-3 px-2 py-1 bg-background/80 backdrop-blur-sm rounded-full">
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <Sparkles size={12} className="text-accent" />
                    AI Generated
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 border-border"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Try Another
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  className="border-border"
                >
                  <Download size={16} />
                </Button>
                <Button
                  onClick={handleShare}
                  className="flex-1 bg-foreground text-background hover:bg-accent hover:text-accent-foreground"
                >
                  <Share2 size={16} className="mr-2" />
                  Share
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Insufficient Credits Modal */}
      {showInsufficientCredits && (
        <InsufficientCreditsModal
          action="virtual_tryon"
          onClose={() => setShowInsufficientCredits(false)}
          onBuyCredits={() => {
            setShowInsufficientCredits(false);
            onBuyCredits?.();
          }}
        />
      )}
    </Dialog>
  );
};
